import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X, Phone, Globe, ChevronDown } from 'lucide-react';
import { SERVICES } from '../data/services';

const NAV = [
  { label: 'Accueil', hash: '#top' },
  { label: 'Copropriétés', hash: '#coproprietes' },
  { label: 'Réalisations', route: '/realisations' },
  { label: 'Pros & Partenaires', route: '/partenaires-professionnels' },
  { label: 'Contact', hash: '#contact' },
];

const SERVICE_NAV = [
  ...SERVICES.map((s) => ({ label: s.nav, route: s.path })),
  { label: 'Rénovation énergétique', route: '/renovation-energetique' },
];

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goToHash = (hash) => {
    setIsMenuOpen(false);
    if (location.pathname !== '/') {
      navigate('/' + (hash === '#top' ? '' : hash));
      return;
    }
    if (!hash || hash === '#top') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleClick = (item) => {
    if (item.route) {
      setIsMenuOpen(false);
      navigate(item.route);
    } else {
      goToHash(item.hash);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200">
      {/* Top bar */}
      <div className="bg-slate-800 text-white py-2">
        <div className="container mx-auto px-4 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4">
            <a href="tel:+33185097125" className="flex items-center gap-2 hover:text-emerald-400 transition-colors">
              <Phone className="h-4 w-4" />
              <span className="hidden sm:inline">01 85 09 71 25</span>
            </a>
            <a href="https://vireo-renovation.fr" className="flex items-center gap-2 hover:text-emerald-400 transition-colors">
              <Globe className="h-4 w-4" />
              <span className="hidden sm:inline">vireo-renovation.fr</span>
            </a>
          </div>
          <div className="text-xs text-slate-300">Paris &amp; Île-de-France</div>
        </div>
      </div>

      {/* Main navigation */}
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" onClick={() => window.scrollTo({ top: 0 })} data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_facade-vireo/artifacts/isyxm6xn_VIREO_logo_compresse%CC%81.png"
              alt="VIREO Logo"
              className="h-12 w-auto"
            />
            <div className="hidden xl:block border-l border-slate-300 pl-3">
              <p className="text-sm text-slate-600 leading-tight">
                Entreprise de rénovation<br />
                Paris &amp; Île-de-France
              </p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {NAV.map((item, idx) => (
              <React.Fragment key={item.label}>
                <button
                  onClick={() => handleClick(item)}
                  data-testid={`nav-${item.label}`}
                  className="text-slate-700 hover:text-emerald-600 transition-colors font-medium text-[15px]"
                >
                  {item.label}
                </button>
                {idx === 0 && (
                  <div className="relative group">
                    <button
                      className="flex items-center gap-1 text-slate-700 hover:text-emerald-600 transition-colors font-medium text-[15px]"
                      data-testid="nav-prestations"
                    >
                      Prestations <ChevronDown className="h-4 w-4" />
                    </button>
                    <div className="absolute left-0 top-full pt-3 hidden group-hover:block z-50">
                      <div className="bg-white border border-slate-200 rounded-xl shadow-xl py-2 w-72">
                        {SERVICE_NAV.map((s) => (
                          <Link
                            key={s.route}
                            to={s.route}
                            data-testid={`nav-service-${s.route}`}
                            className="block px-4 py-2.5 text-slate-700 hover:bg-slate-50 hover:text-emerald-700 transition-colors text-sm"
                          >
                            {s.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </React.Fragment>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Button
              onClick={() => goToHash('#contact')}
              data-testid="header-cta"
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white px-6 py-2 transition-all duration-300"
            >
              Devis gratuit
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden text-slate-700"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white border-t border-slate-200 max-h-[80vh] overflow-y-auto">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            <button
              onClick={() => handleClick(NAV[0])}
              data-testid="mobile-nav-Accueil"
              className="text-left py-2 text-slate-700 hover:text-emerald-600 transition-colors font-medium"
            >
              Accueil
            </button>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide pt-3 pb-1">Prestations</p>
            {SERVICE_NAV.map((s) => (
              <Link
                key={s.route}
                to={s.route}
                onClick={() => setIsMenuOpen(false)}
                data-testid={`mobile-nav-service-${s.route}`}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 transition-colors font-medium"
              >
                {s.label}
              </Link>
            ))}
            <div className="border-t border-slate-100 my-2" />
            {NAV.slice(1).map((item) => (
              <button
                key={item.label}
                onClick={() => handleClick(item)}
                data-testid={`mobile-nav-${item.label}`}
                className="text-left py-2 text-slate-700 hover:text-emerald-600 transition-colors font-medium"
              >
                {item.label}
              </button>
            ))}
            <Button
              onClick={() => goToHash('#contact')}
              className="bg-gradient-to-r from-emerald-600 to-cyan-600 hover:from-emerald-700 hover:to-cyan-700 text-white w-full transition-all duration-300 mt-2"
            >
              Devis gratuit
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
